<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-card flat>
    <v-card-title class="title font-weigth-light px-0 py-0 mb-4">
      Vehicle Booking Details
    </v-card-title>
    <v-card-text class="px-0">
      <v-row>
        <v-col cols="3">
          <date-picker
            v-model="datePicker.start"
            :masks="datePicker.masks"
            :min-date="datePicker.minimum"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="From Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'Start Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="3">
          <date-picker
            v-model="datePicker.end"
            :masks="datePicker.masks"
            :min-date="datePicker.start"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="To Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'End Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="3">
          <v-combobox
            v-model="selectedAssetGroups"
            :items="assetGroups"
            item-text="name"
            item-value="id"
            label="Asset Group"
            multiple
            outlined
            required
            @change="changeAssetGroup; checkAllAssetGroup(false)"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllAssetGroup(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllAssetGroup }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllAssetGroup }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedAssetGroups.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedAssetGroups.length - 1 }} Others)
              </span>
            </template>
          </v-combobox>
        </v-col>
        <v-col cols="3">
          <v-combobox
            v-model="selectedVehicle"
            :items="filteredVehicles"
            item-text="device_name"
            item-value="id"
            label="Vehicle"
            multiple
            outlined
            required
            @change="(items) => { changeVehicle(items); checkAllVehicle(false) }"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllVehicle(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllVehicle }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllVehicle }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.device_name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedVehicle.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedVehicle.length - 1 }} Others)&nbsp;
              </span>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-end"
        >
          <v-btn
            border
            color="primary"
            @click="generateReport"
          >
            {{ isGenerate && isLoading ? 'Generating' : 'Generate' }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && selectedVehicleForTabs.length">
        <v-col cols="12">
          <v-tabs
            bg-color="primary"
            v-model="selectedTab"
          >
            <v-tab
              v-for="tab in selectedVehicleForTabs"
              :key="tab.id"
              :value="tab.id"
              @click="generateReport(tab.id)"
            >
              {{ tab.device_name }}
            </v-tab>
          </v-tabs>
          <v-data-table
            :headers="headers"
            :items="dataPerTab"
            class="font-weight-light pa-5"
            :loading="isLoading"
            loading-text="Loading"
            :footer-props="footerProps"
            :options.sync="paginationOptions"
          >
            <template #[`item.book_detail.start_geofence_id`]="{ item }">
              {{ getOriginName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.vehicle_name`]="{ item }">
              {{ getVehicleName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_geofence_id`]="{ item }">
              {{ getDestinationName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.start_datetime`]="{ item }">
              {{ startDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_datetime`]="{ item }">
              {{ endDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.created_at`]="{ item }">
              {{ formatDate(item.book_detail.created_at) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row v-if="isLoading && selectedVehicleForTabs.length === 0">
        <v-col cols="12">
          <p class="text-center">
            Loading
          </p>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && !isLoading && selectedVehicleForTabs.length === 0">
        <v-col cols="12">
          <p class="text-center">
            No Data
          </p>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import get from "lodash/get";
import { getBookingDestinationText } from "@/plugins/destination";

// Client-side only. selectedAssetGroups never reaches the API, so this id is
// never sent anywhere.
const UNGROUPED_GROUP_ID = "__UNGROUPED__";

export default {
  data() {
    return {
      assetGroups: [],
      vehicles: [],
      assetTypes: [],
      geofences: [],
      selectedVehicle: [],
      selectedAssetGroups: [],
      selectedVehicleForTabs: [],
      datePicker: {
        start: '',
        startUnix: '',
        paramStart: '',
        end: '',
        endUnix: '',
        paramEnd: '',
        masks: {
          input: 'DD MMMM YYYY'
        },
      },
      selectedTab: '',
      headers: [
        { text: "Booking ID", value: "book_detail.id" },
        { text: 'First Name', align: 'left', value: 'first_name' },
        { text: 'Last Name', align: 'left', value: 'last_name' },
        { text: "From", value: "book_detail.start_geofence_id" },
        { text: "To", value: "book_detail.end_geofence_id" },
        { text: "Start Date", value: "book_detail.start_datetime" },
        { text: "End Date", value: "book_detail.end_datetime" },
        { text: "Asset Name", value: "book_detail.vehicle_name" },
        { text: "Created At", value: "book_detail.created_at"},
      ],
      footerProps: {
        itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
      },
      paginationOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      data: [],
      isLoading: false,
      isGenerate: false,
      timeout: 0,
      textCheckAllAssetGroup: 'CHECK ALL ASSET GROUP',
      iconCheckAllAssetGroup: 'mdi-check-all',
      textCheckAllVehicle: 'CHECK ALL VEHICLE',
      iconCheckAllVehicle: 'mdi-check-all',
    };
  },
  props: {
    params: {
      type: Object,
      required: false,
      default: () => ({
        vehicles: [],
        assetGroups: [],
        assetTypes: [],
        geofences: [],
      }),
    },
  },
  mounted() {
    this.vehicles = this.params.vehicles;
    this.assetTypes = this.params.assetTypes;
    this.assetGroups = this.params.assetGroups.filter((group) => {
      const assetType = this.params.assetTypes.find((type) => type.id === group.metadata.asset_type);
      return assetType && assetType.is_vehicle == '1';
    });
    this.addUngroupedAssetGroup();
    this.geofences = this.params.geofences;
  },
  watch: {
    "datePicker.start"(value) {
      const startDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramStart = startDate;
    },
    "datePicker.end"(value) {
      const endDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramEnd = endDate;
    },
  },
  computed: {
    dataPerTab() { 
      const vehicle = this.selectedVehicleForTabs[this.selectedTab];
      return this.data.filter((item) => item.book_detail.vehicle_id == vehicle.id);
    },
    filteredVehicles() {
      if (this.selectedAssetGroups.length) {
        return this.vehicles.filter(
          (vehicle) => this.selectedAssetGroups.some(
            (group) => {
              return group.members && group.members.find((member) => member.detail.id === vehicle.id);
            }
          )
        );
      }
      return this.vehicles;
    },
    isWSP() {
      return this.$store.state.clientid == 'H1O5G7P0N3JA';
    },
    isIntimap() {
      return this.$store.state.clientid == 'DSSTWWLLIHH7';
    },
  },
  methods: {
    checkAllAssetGroup(force = false) {
      if (force) {
        if (this.textCheckAllAssetGroup === 'CHECK ALL ASSET GROUP') {
          this.selectedAssetGroups = this.assetGroups;
          this.textCheckAllAssetGroup = 'UNCHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-close';
        } else {
          this.selectedAssetGroups = [];
          this.textCheckAllAssetGroup = 'CHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-check-all';
        }
      } else {
        if (this.selectedAssetGroups.length === this.assetGroups.length) {
          this.textCheckAllAssetGroup = 'UNCHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-close';
        } else {
          this.textCheckAllAssetGroup = 'CHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-check-all';
        }
      }
    },
    // A vehicle in none of the groups above cannot be reached once any Asset
    // Group is selected, because filteredVehicles keeps only group members.
    // Retiring a vehicle drops it out of its group, so its booking history
    // becomes impossible to pull from this report. Offer those vehicles as an
    // explicit choice rather than folding them into every filter, which would
    // put every retired vehicle in front of the user on every report.
    addUngroupedAssetGroup() {
      const grouped = new Set();
      this.assetGroups.forEach((group) => {
        (group.members || []).forEach((member) => {
          if (member && member.detail) grouped.add(member.detail.id);
        });
      });

      const ungrouped = this.vehicles.filter((vehicle) => !grouped.has(vehicle.id));
      if (!ungrouped.length) return;

      this.assetGroups = [
        {
          id: UNGROUPED_GROUP_ID,
          name: `— No Asset Group (${ungrouped.length}) —`,
          members: ungrouped.map((vehicle) => ({ detail: vehicle })),
        },
        ...this.assetGroups,
      ];
    },
    changeAssetGroup() {
      this.selectedAssetGroups = this.selectedAssetGroups.filter((assetGroup) => typeof assetGroup === 'object' && assetGroup.id);
    },
    checkAllVehicle(force = false) {
      if (force) {
        if (this.textCheckAllVehicle === 'CHECK ALL VEHICLE') {
          this.selectedVehicle = this.filteredVehicles;
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.selectedVehicle = [];
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      } else {
        if (this.selectedVehicle.length === this.filteredVehicles.length) {
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      }
    },
    changeVehicle(items) {
      this.selectedVehicle = items.filter((vehicle) => typeof vehicle === 'object' && vehicle.id);
    },
    generateReport(vehicleId) {
      if (!this.selectedVehicle.length || !this.datePicker.start || !this.datePicker.end) return;
      if (this.selectedVehicle.length && this.datePicker.start && this.datePicker.end) {
        let selectedVehicleId = null;
        if (typeof vehicleId !== 'object') {
          selectedVehicleId = vehicleId;
        }
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.isLoading = true;
          const promise = selectedVehicleId == null ? this.getDataBookTabs() : Promise.resolve();
          promise.then(() => {
            if (selectedVehicleId == null) {
              if (this.selectedVehicleForTabs.length) {
                this.selectedTab = 0;
                this.getDataBook(this.selectedVehicleForTabs[0].id);
              } else {
                this.data = [];
                this.isGenerate = true;
                this.isLoading = false;
              }
            } else {
              this.getDataBook(selectedVehicleId);
            }
          })
        }, 500);
      }
    },
    getDataBookTabs() {
      return this.$store.dispatch('API_CALL', {
        $method: 'get_book_for_tabs',
        $postData: {
          vehicle_id: this.selectedVehicle.map((vehicle) => vehicle.id),
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.selectedVehicleForTabs = response.data.data.map((vehicle) => this.selectedVehicle.find((v) => v.id === vehicle.vehicle_id));
      });
    },
    getDataBook(vehicleId) {
      this.isLoading = true;
      const payload = {
        vehicle_id: vehicleId,
        clientid: this.$store.state.clientid,
        start_date: this.datePicker.paramStart,
        end_date: this.datePicker.paramEnd,
        get_schedules: 1,
        get_recurring: 0,
      };
      if (this.isIntimap || this.isWSP) {
        payload.use_timezone_depot = 1;
      }
      return this.$store.dispatch('API_CALL', {
        $method: 'get_all_book',
        $postData: payload,
      })
      .then((response) => {
        this.data = response.data.data;
        this.isGenerate = true;
        this.isLoading = false;
      });
    },
    getOriginName(item) {
      let destination = '';
      if (item.start_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.start_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        try {
          const metadata = JSON.parse(item.metadata);
          destination = metadata.hasOwnProperty('address') ? metadata.address : '';
        } catch (e) {}
      }
      return destination;
    },
    getVehicleName(item) {
      if (item == null) return;
      let metadata = '';
      if (!isNull(item.vehicle_name)) {
        metadata = `Vehicle - ${item.vehicle_name}`;
      } else if (!isNull(item.asset_id)) {
        const typeName = this.assetTypes.find((d) => d.id == item.asset_type);
        metadata = `${get(typeName, 'name', '-')} - ${item.asset_number}`;
      } else {
        metadata = '--';
      }
      if (!metadata) return;
      return metadata;
    },
    getDestinationName(item) {
      let destination = '';
      if (item.end_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.end_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        destination = getBookingDestinationText(item.metadata, 2);
      }
      return destination;
    },
    startDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("start_datetime")) {
        date = moment(item.start_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    endDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("end_datetime")) {
        date = moment(item.end_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    formatDate(date, format = 'D MMM YYYY HH:mm') {
      if (date == null) return;
      const dateFormatted = moment(date).format(format)
      return dateFormatted;
    },
  },
};
</script>
